import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import classNames from 'classnames';
import { ConfigContext } from '../config-provider';
import CarouselSwitch from './switch';
import CarouselIndicator from './indicator';
const Carousel = ({ className, style, children, dotPosition = 'bottom', playInterval = 5000, autoplay = true, width = 1920, dotType = 'dot', animationTime = 400, multi, showNumber = 3, multiShift = false, indicatorStyle, infinite = true }) => {
    const carouselRef = useRef();
    const timer = useRef();
    const [isHover, setIsHover] = useState(false);
    const { getPrefixCls } = React.useContext(ConfigContext);
    const prefixCls = getPrefixCls('carousel');
    // 判断子元素是否是数组，如果不是，那就不存在轮播的情况
    const childrenIsArray = useMemo(() => Array.isArray(children), [children]);
    // 因为要实现无缝轮播，所以前后会增加一个key。例如对用户看到有3张图片，但渲染中前后会添加得到5张
    const extraCount = multi ? showNumber : 1;
    const [{ lastKey, currentKey }, setKey] = useState({
        lastKey: extraCount,
        currentKey: extraCount
    });
    const length = Array.isArray(children) ? children.length : undefined;
    const next = useCallback(() => {
        // 向右滑动，最后一帧无缝滑动到第一帧使用的是额外添加的帧，滑动动画结束后，立刻切换到原始帧
        if (infinite) {
            if (currentKey === length + extraCount) {
                setKey({ lastKey: currentKey, currentKey: extraCount });
                setTimeout(() => {
                    // 重置掉自动轮播
                    clearTimeout(timer.current);
                    const nextKey = extraCount + (multiShift ? extraCount : 1);
                    setKey({
                        lastKey: extraCount,
                        currentKey: nextKey > length + extraCount ? length + extraCount : nextKey
                    });
                }, 100);
            }
            else {
                const nextKey = currentKey + (multiShift ? extraCount : 1);
                setKey({
                    lastKey: currentKey,
                    currentKey: nextKey > length + extraCount ? length + extraCount : nextKey
                });
            }
        }
        else {
            const nextKey = currentKey + (multiShift ? extraCount : 1);
            setKey({
                lastKey: currentKey,
                currentKey: nextKey > length + extraCount ? length + extraCount : nextKey
            });
        }
    }, [currentKey, extraCount, length, multiShift, infinite]);
    // 向左滑动，第一帧无缝滑动到最后一帧使用的是额外添加的帧，滑动动画结束后，立刻切换到原始帧
    const prev = useCallback(() => {
        if (infinite) {
            if (currentKey === 0) {
                setKey({ lastKey: currentKey, currentKey: length });
                setTimeout(() => {
                    // 重置掉自动轮播
                    clearTimeout(timer.current);
                    const nextKey = length - (multiShift ? extraCount : 1);
                    setKey({
                        lastKey: length,
                        currentKey: nextKey < 0 ? 0 : length - (multiShift ? extraCount : 1)
                    });
                });
            }
            else {
                const nextKey = currentKey - (multiShift ? extraCount : 1);
                setKey({
                    lastKey: currentKey,
                    currentKey: nextKey < 0 ? 0 : currentKey - (multiShift ? extraCount : 1)
                });
            }
        }
        else {
            const nextKey = currentKey - (multiShift ? extraCount : 1);
            setKey({
                lastKey: currentKey,
                currentKey: nextKey < 0 ? 0 : nextKey
            });
        }
    }, [currentKey, extraCount, length, multiShift, infinite]);
    // 监听是否hover热区，hover情况下不会自动轮播
    useEffect(() => {
        carouselRef.current.addEventListener('mouseover', () => {
            setIsHover(true);
        });
        carouselRef.current.addEventListener('mouseleave', () => {
            setIsHover(false);
        });
    });
    // 自动轮播
    useEffect(() => {
        clearTimeout(timer.current);
        if (length > 1 && autoplay && !isHover) {
            timer.current = setTimeout(next, playInterval);
        }
        return () => clearTimeout(timer.current);
    }, [autoplay, length, playInterval, next, isHover]);
    const getDuration = useCallback(() => {
        // next中立即切换的情况
        if ((lastKey === length + extraCount) && (currentKey === extraCount)) {
            return '0s';
        }
        // prev中立即切换的情况
        if (lastKey === 0 && currentKey === length) {
            return '0s';
        }
        // 正常切换的情况
        return `${animationTime / 1000}s`;
    }, [lastKey, length, extraCount, currentKey, animationTime]);
    // 渲染的元素数组，首位增加实现无缝轮播
    const renderChildren = useMemo(() => {
        let elements = Array.isArray(children) ? children : [children];
        if (elements.length >= 1) {
            const extraPre = elements.slice(elements.length - extraCount);
            const extraPost = elements.slice(0, extraCount);
            elements = [...extraPre, ...elements, ...extraPost];
        }
        return elements;
    }, [children, extraCount]);
    const cardWidth = useMemo(() => {
        if (multi) {
            return (width - 4 * (extraCount - 1)) / extraCount;
        }
        return width;
    }, [multi, width, extraCount]);
    const getLeft = (idx) => {
        return (multi ? (cardWidth + 8) : cardWidth) * (idx - currentKey);
    };
    const wrapperClassName = classNames(prefixCls, className, {
        [`${prefixCls}-multi`]: multi
    });
    const isPrevDisabled = !infinite && currentKey === extraCount;
    const isNextDisabled = !infinite && currentKey === length;
    return (<div ref={carouselRef} style={style} className={wrapperClassName}>
            {childrenIsArray ? (<>
                    <CarouselSwitch prefixCls={prefixCls} next={next} prev={prev} isPrevDisabled={isPrevDisabled} isNextDisabled={isNextDisabled}/>
                    {!multi && (<CarouselIndicator prefixCls={prefixCls} setKey={setKey} length={length} currentKey={currentKey} autoplay={autoplay} playInterval={playInterval} dotPosition={dotPosition} dotType={dotType} multi={multi} showNumber={showNumber} style={indicatorStyle}/>)}
                    <div className={`${prefixCls}-content`}>
                        <ul>
                            {renderChildren.map((child, idx) => (<li key={idx} style={{
                    left: getLeft(idx),
                    width: cardWidth,
                    transitionDuration: getDuration()
                }}>
                                    {child}
                                </li>))}
                        </ul>
                    </div>
                </>) : children}
        </div>);
};
export default Carousel;
